import { SHIPS, getShipById, type ShipSpec } from "../config/ships";
import { TRACKS, type TrackSpec } from "../config/tracks";
import { previewCanvas, previewsLive, startTrackPreviews } from "./trackPreviews";
import { logoMark, ICONS, shipIcon, trackThumb } from "./marks";
import { ShipPreview } from "./ShipPreview";
import type { AudioManager, Pool } from "../audio/AudioManager";
import { schemeLabel, type Scheme } from "../input/PlayerInput";
import { pilot } from "../account/pilot";
import { loadStats } from "../account/pilotStats";
import { esc, netStatusHtml, pilotCardHtml, statsHtml, type NameEdit } from "./profileView";
import { onlineBodyHtml, type OnlineView } from "./onlineView";
import type { UiSound } from "../audio/uiSounds";
import { mountXpWindowBadge } from "@act/client-core/xpWindow";

type Screen = "main" | "garage" | "course" | "music" | "local" | "mp" | "solo" | "build" | "profile" | "online";
/** The race being set up: pick the mode, then the course, then the craft. */
type PendingRace = "quick" | "time" | "local";

interface GameMode {
  id: string;
  name: string;
  jp: string;
  icon: string;
  /** false = placeholder (Grand Prix / Multiplayer aren't built yet). */
  playable: boolean;
}

/** Condensed top-level menu. SOLO + MULTIPLAYER open submenus. */
const MODES: GameMode[] = [
  { id: "solo", name: "SOLO", jp: "ソロ", icon: ICONS.quick, playable: true },
  { id: "mp", name: "MULTIPLAYER", jp: "マルチプレイヤー", icon: ICONS.mp, playable: true },
  { id: "garage", name: "GARAGE", jp: "ガレージ", icon: ICONS.garage, playable: true },
  { id: "profile", name: "PROFILE", jp: "プロフィール", icon: ICONS.pilot, playable: true },
  { id: "music", name: "MUSIC", jp: "ミュージック", icon: ICONS.music, playable: true },
];

/** Solo submenu modes. */
const SOLO_MODES: GameMode[] = [
  { id: "quick", name: "QUICK RACE", jp: "クイックレース", icon: ICONS.quick, playable: true },
  { id: "time", name: "TIME ATTACK", jp: "タイムアタック", icon: ICONS.time, playable: true },
  { id: "gp", name: "GRAND PRIX", jp: "グランプリ", icon: ICONS.gp, playable: false },
];

/** Track-builder submenu — three editor modes of the one tool. */
/** Course cards per row (pad up/down moves a row). */
const COURSE_COLS = 3;

const BUILD_MODES: GameMode[] = [
  { id: "mapmaker", name: "SIMPLE", jp: "シンプル · グリッド", icon: ICONS.track, playable: true },
  { id: "tiles", name: "TILES", jp: "タイル · パーツ", icon: ICONS.blocks, playable: true },
  { id: "editor", name: "COMPLEX", jp: "コンプレックス · フリーフォーム", icon: ICONS.editor, playable: true },
];

function hex(c: ShipSpec): string {
  return c.color.toHexString();
}

/**
 * VECTOR DRIFT front-end. Two screens:
 *  - main: a vertical list of game modes + a 3D hero ship.
 *  - garage: ship roster carousel + stat readout + 3D ship on a neon stage.
 * The chosen ship + control prefs persist across screens. A persistent preview
 * canvas (its own little Babylon view) is overlaid on the hero region of
 * whichever screen is up, so re-rendering the DOM never tears down the 3D view.
 */
export class Menu {
  private root: HTMLDivElement;
  private content: HTMLDivElement;
  private stage: HTMLDivElement;
  private canvas: HTMLCanvasElement;
  private preview: ShipPreview;

  private screen: Screen = "main";
  private selectedShipId = SHIPS[0].id;
  private selectedTrackId = TRACKS[0].id;
  /** Set while a race is being set up (mode → course → craft); null otherwise. */
  private pending: PendingRace | null = null;
  /** Currently focused main-menu row (keyboard/gamepad cursor). */
  private focus = "solo";
  /** Cursor on the SOLO submenu (index into SOLO_MODES). */
  private soloFocus = 0;
  /** The double XP badge, in the main screen's top bar while a window runs (ACT HQ t-1413); made once, so its
   * countdown carries on across re-renders. */
  private readonly xpHost: HTMLDivElement = document.createElement("div");
  /** Cursor on the TRACK BUILDER submenu (index into BUILD_MODES). */
  private buildFocus = 0;
  private useGyro = false;
  /** Gamepad cursor on the music screen (0 = skip button, 1.. = track rows). */
  private musicFocus = 0;
  /** Local split-screen join lobby: each slot is a device that claimed it, the
   * craft it's chosen, and whether it has confirmed (ready). Join order = P1.. */
  private lobby: { scheme: Scheme; shipId: string; ready: boolean }[] = [];
  /** Per-gamepad previous button state for edge detection. */
  private padPrev = new Map<number, { a: boolean; b: boolean; start: boolean; left: boolean; right: boolean }>();
  /** Cursor on the multiplayer submenu (0 = local, 1 = online). */
  private mpFocus = 0;
  /** Set on a screen change so render() plays the enter animation once. */
  private entering = false;
  /** Cursor on the profile screen (0 = name, 1 = account). */
  private profileFocus = 0;
  /** The profile's name field, while it's open. */
  private nameEdit: NameEdit | null = null;
  /** The online race being joined, while its screen is up. */
  private onlineView: OnlineView | null = null;
  /** A menu sound (Game's UiSounds): the toggles' and refusals' own; presses sound by themselves. */
  sfx: (sound: UiSound) => void = () => {};
  /** MULTIPLAYER · ONLINE: join the next race (Game). */
  onOnline: () => void = () => {};
  /** BACK from the online screen: leave the race (Game). */
  onOnlineLeave: () => void = () => {};

  constructor(
    container: HTMLElement,
    private isTouchDevice: boolean,
    private onStart: (ship: ShipSpec, mode: string, useGyro: boolean, trackId: string) => void,
    private onEditor: () => void,
    private onSimpleEditor: () => void,
    private onTileEditor: () => void,
    private audio: AudioManager,
    private onStartLocal: (entries: { scheme: Scheme; shipId: string }[], trackId: string) => void,
    /** The track builder's own page (/builder): it opens on the builder, and BACK goes to the game.
     * The builder left the game's menu on 10 Oct 2026 (Theo: "move them to a sub URL"). */
    private builderOnly = false
  ) {
    this.root = document.createElement("div");
    this.root.className = "vd-menu overlay";
    // Theo, 29 Oct 2026: "put a small thing saying double EXP weekend with a countdown to when it ends on the
    // different games' main menus. It can just be like a subtle thing".
    this.xpHost.className = "vd-xp";
    mountXpWindowBadge(this.xpHost);

    this.stage = document.createElement("div");
    this.stage.className = "vd-stage";
    this.canvas = document.createElement("canvas");
    this.canvas.className = "vd-preview";
    this.stage.appendChild(this.canvas);

    this.content = document.createElement("div");
    this.content.className = "vd-content";

    this.root.append(this.stage, this.content);
    container.appendChild(this.root);

    this.preview = new ShipPreview(this.canvas);
    window.addEventListener("resize", () => this.positionPreview());
    // Keyboard join/start for the split-screen lobby (only acts on that screen).
    window.addEventListener("keydown", (e) => this.lobbyKey(e));
    // Esc closes the ACT Account panel.
    window.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && pilot.accountOpen()) pilot.closeAccount();
    });
    // The ACT account answering (or changing) shows on the main menu and the profile.
    pilot.onChange(() => this.onPilotChanged());
    pilot.onEditName = () => {
      if (this.screen !== "profile") this.goto("profile");
      this.startNameEdit();
    };

    this.render();
  }

  private render(): void {
    if (this.screen === "main") this.renderMain();
    else if (this.screen === "garage") this.renderGarage();
    else if (this.screen === "course") this.renderCourse();
    else if (this.screen === "music") this.renderMusic();
    else if (this.screen === "local") this.renderLocal();
    else if (this.screen === "mp") this.renderMp();
    else if (this.screen === "build") this.renderBuild();
    else if (this.screen === "profile") this.renderProfile();
    else if (this.screen === "online") this.renderOnline();
    else this.renderSolo();
    // Play the screen-enter animation only on an actual screen change, not on
    // the frequent in-screen re-renders (cursor moves, ship cycles, lobby ticks).
    if (this.entering) {
      this.content.querySelector(".vd-shell")?.classList.add("vd-enter");
      this.entering = false;
    }
    // Main menu is a live autopilot flythrough; garage/tracks spin the model.
    this.preview.setMode(this.screen === "main" ? "drive" : "showcase");
    this.preview.setShip(getShipById(this.selectedShipId));
    this.preview.setRing(this.screen === "garage");
    this.positionPreview();
  }

  private trackName(id: string): string {
    return TRACKS.find((t) => t.id === id)?.name ?? "—";
  }

  /** Overlay the preview canvas on the current screen's hero element — or, on the
   * main menu, full-bleed behind everything so the autopilot flythrough is an
   * immersive backdrop rather than a boxed-in panel. */
  private positionPreview(): void {
    if (this.root.style.display === "none") {
      this.canvas.style.display = "none";
      return;
    }
    if (this.screen === "main") {
      this.canvas.style.display = "";
      this.canvas.style.left = "0";
      this.canvas.style.top = "0";
      this.canvas.style.width = "100%";
      this.canvas.style.height = "100%";
      this.preview.resize();
      return;
    }
    const sel = this.screen === "garage" ? ".vd-garage-hero" : null;
    const hero = sel ? (this.content.querySelector(sel) as HTMLElement | null) : null;
    if (!hero) {
      this.canvas.style.display = "none";
      return;
    }
    const r = hero.getBoundingClientRect();
    const base = this.root.getBoundingClientRect();
    this.canvas.style.display = "";
    this.canvas.style.left = `${r.left - base.left}px`;
    this.canvas.style.top = `${r.top - base.top}px`;
    this.canvas.style.width = `${r.width}px`;
    this.canvas.style.height = `${r.height}px`;
    this.preview.resize();
  }

  private plusMarks(): string {
    return `
      <span class="vd-plus vd-p1">+</span>
      <span class="vd-plus vd-p2">+</span>
      <span class="vd-plus vd-p3">+</span>
      <span class="vd-plus vd-p4">+</span>`;
  }

  /** The vertical edge rails + corner accent the main menu carries, so the
   * submenu screens read as intentionally airy rather than empty. */
  private edgeRails(left: string): string {
    return `
      <span class="vd-side-label" style="top:30vh;left:1.6vh">${left}</span>
      <span class="vd-side-label" style="top:30vh;right:1.6vh">VECTOR DRIFT SYSTEM</span>
      <div class="vd-corner tl"></div>`;
  }

  // ---- main: game modes ----------------------------------------------------

  /** Compact segmented stat bars (shared by garage + desktop hero info). */
  private miniStats(ship: ShipSpec): string {
    const seg = (v: number) => {
      const on = Math.round(v * 8);
      let o = "";
      for (let i = 0; i < 8; i++) o += `<i class="${i < on ? "on" : ""}"></i>`;
      return `<div class="vd-seg">${o}</div>`;
    };
    const row = (l: string, v: number) => `<div class="vd-hi-stat"><span>${l}</span>${seg(v)}</div>`;
    return (
      row("TRN", ship.cornering) +
      row("ACC", ship.acceleration) +
      row("TOP", ship.topSpeed) +
      row("WGT", ship.weight)
    );
  }

  private renderMain(): void {
    this.root.className = "vd-menu overlay vd-screen-main";
    const ship = getShipById(this.selectedShipId);

    const modes = this.visibleModes().map((m) => {
      const sel = m.id === this.focus;
      const sub = m.id === "tracks" ? `${m.jp} · ${this.trackName(this.selectedTrackId)}` : m.jp;
      return `
      <button class="vd-mode ${sel ? "sel" : ""} ${m.playable ? "" : "soon"}" data-mode="${m.id}">
        <span class="vd-ic">${m.icon}</span>
        <span>
          <span class="vd-mode-name">${m.name}</span>
          <span class="vd-mode-jp">${sub}</span>
        </span>
        ${m.playable ? "" : `<span class="soon-tag">SOON</span>`}
      </button>`;
    }).join("");

    this.content.innerHTML = `
      <div class="vd-shell">
        <span class="vd-side-label" style="top:30vh;left:1.6vh">ENTER MENU</span>
        <span class="vd-side-label" style="top:30vh;right:1.6vh">VECTOR DRIFT SYSTEM</span>
        ${this.plusMarks()}
        <div class="vd-corner tl"></div>

        <header class="vd-topbar">
          <div class="vd-brand">
            <span class="vd-badge">${logoMark()}</span>
            <span>
              <span class="vd-brand-name">VECTOR DRIFT</span>
              <span class="vd-brand-jp">ベクタードリフト</span>
            </span>
          </div>
          <button class="vd-meta vd-pilot-chip" aria-label="Pilot profile">
            <span class="vd-meta-txt">
              <span class="vd-meta-label">PILOT</span>
              <span class="vd-meta-val">${esc(pilot.label())}</span>
            </span>
            <span class="vd-plusbox">+</span>
            <span class="tag vd-hatch-pink"></span>
          </button>
        </header>

        <div class="vd-menu-body">
          <nav class="vd-modes">
            <div class="vd-modes-bracket"></div>
            ${modes}
          </nav>
          <div class="vd-hero">
            <div class="vd-hero-info">
              <div class="vd-hi-name">${ship.code} <span>${ship.name}</span></div>
              ${this.miniStats(ship)}
            </div>
          </div>
        </div>

        <footer class="vd-botbar">
          <button class="vd-act select-act"><span class="ring">✕</span> SELECT</button>
          <div class="vd-status">
            ${this.isTouchDevice ? `<button class="vd-gyro ${this.useGyro ? "on" : ""}">TILT ${this.useGyro ? "ON" : "OFF"}</button>` : ""}
            ${netStatusHtml(pilot.state)}
          </div>
        </footer>
      </div>`;
    this.content.querySelector(".vd-topbar .vd-brand")?.after(this.xpHost);

    this.content.querySelectorAll<HTMLButtonElement>(".vd-mode").forEach((btn) => {
      btn.addEventListener("click", () => this.pickMode(btn.dataset.mode!));
    });
    this.content
      .querySelector<HTMLButtonElement>(".select-act")!
      .addEventListener("click", () => this.activate(this.focus));
    this.content.querySelector<HTMLButtonElement>(".vd-gyro")?.addEventListener("click", () => {
      this.useGyro = !this.useGyro;
      this.sfx(this.useGyro ? "toggle-on" : "toggle-off");
      this.render();
    });
    this.content.querySelector<HTMLButtonElement>(".vd-pilot-chip")!.addEventListener("click", () => this.pickMode("profile"));
  }

  /** Top-level modes (all shown — split-screen works wherever there's a
   * keyboard/gamepad, and `isTouchDevice` over-reports on touchscreen laptops). */
  private visibleModes(): GameMode[] {
    return MODES;
  }

  /** IDs the cursor can land on (playable rows, incl. Garage). */
  private navIds(): string[] {
    return this.visibleModes().filter((m) => m.playable).map((m) => m.id);
  }

  /** Click a row: focus it and act immediately. */
  private pickMode(id: string): void {
    if (!this.navIds().includes(id)) return;
    this.focus = id;
    this.activate(id);
  }

  /** Act on a top-level row: open the matching submenu / garage / editor. */
  private activate(id: string): void {
    if (id === "solo") {
      this.soloFocus = 0;
      this.goto("solo");
    } else if (id === "mp") {
      this.mpFocus = 0;
      this.goto("mp");
    } else if (id === "garage") {
      this.pending = null;
      this.goto("garage");
    } else if (id === "profile") {
      this.openProfile();
    } else if (id === "build") {
      this.buildFocus = 0;
      this.goto("build");
    } else if (id === "music") {
      this.musicFocus = 0;
      this.goto("music");
    }
  }

  /** Act on a SOLO submenu row: set up that race (course next), or ignore SOON. */
  private soloActivate(id: string): void {
    if (id === "quick" || id === "time") {
      this.pending = id;
      this.goto("course");
    }
  }

  /** Start the solo race that's been set up (from the craft step). */
  private startPending(): void {
    if (this.pending === "quick" || this.pending === "time") {
      this.onStart(getShipById(this.selectedShipId), this.pending, this.useGyro, this.selectedTrackId);
    }
  }

  /** After the course: solo races pick a craft in the garage; local races go
   * to the join lobby, where each player picks their own. */
  private courseNext(): void {
    this.goto(this.pending === "local" ? "local" : "garage");
  }

  private courseBack(): void {
    this.goto(this.pending === "local" ? "mp" : "solo");
  }

  /** BACK from the builder: the main menu, or on the builder's own page the game itself. */
  private buildBack(): void {
    if (this.builderOnly) location.href = "./";
    else this.goto("main");
  }

  /** Act on a TRACK BUILDER submenu row: open the chosen editor mode. */
  private buildActivate(id: string): void {
    if (id === "mapmaker") this.onSimpleEditor();
    else if (id === "tiles") this.onTileEditor();
    else if (id === "editor") this.onEditor();
  }

  /** Move the main-menu cursor by `dir` (-1 up / +1 down), wrapping. */
  private moveFocus(dir: number): void {
    const ids = this.navIds();
    const i = ids.indexOf(this.focus);
    this.focus = ids[(i + dir + ids.length) % ids.length] ?? ids[0];
    this.renderMain();
  }

  /** Drive the menu from a gamepad (or other) navigation event. */
  handlePad(nav: { up: boolean; down: boolean; left: boolean; right: boolean; confirm: boolean; back: boolean }): void {
    if (this.screen === "main") {
      if (nav.up) this.moveFocus(-1);
      if (nav.down) this.moveFocus(1);
      if (nav.confirm) this.activate(this.focus);
    } else if (this.screen === "garage") {
      if (nav.left) this.cycle(-1);
      if (nav.right) this.cycle(1);
      if (this.racing()) {
        if (nav.confirm) this.startPending();
        if (nav.back) this.goto("course");
      } else if (nav.confirm || nav.back) this.goto("main");
    } else if (this.screen === "solo") {
      if (nav.up) {
        this.soloFocus = (this.soloFocus - 1 + SOLO_MODES.length) % SOLO_MODES.length;
        this.renderSolo();
      }
      if (nav.down) {
        this.soloFocus = (this.soloFocus + 1) % SOLO_MODES.length;
        this.renderSolo();
      }
      if (nav.confirm) this.soloActivate(SOLO_MODES[this.soloFocus].id);
      if (nav.back) this.goto("main");
    } else if (this.screen === "build") {
      if (nav.up) {
        this.buildFocus = (this.buildFocus - 1 + BUILD_MODES.length) % BUILD_MODES.length;
        this.renderBuild();
      }
      if (nav.down) {
        this.buildFocus = (this.buildFocus + 1) % BUILD_MODES.length;
        this.renderBuild();
      }
      if (nav.confirm) this.buildActivate(BUILD_MODES[this.buildFocus].id);
      if (nav.back) this.buildBack();
    } else if (this.screen === "course") {
      if (nav.left) this.moveCourse(-1);
      if (nav.right) this.moveCourse(1);
      if (nav.up) this.moveCourse(-COURSE_COLS);
      if (nav.down) this.moveCourse(COURSE_COLS);
      if (nav.confirm) this.courseNext();
      if (nav.back) this.courseBack();
    } else if (this.screen === "mp") {
      if (nav.up || nav.down) {
        this.mpFocus = this.mpFocus === 0 ? 1 : 0;
        this.renderMp();
      }
      if (nav.confirm && this.mpFocus === 0) {
        this.pending = "local";
        this.goto("course");
      }
      if (nav.confirm && this.mpFocus === 1) this.onOnline();
      if (nav.back) this.goto("main");
    } else if (this.screen === "local") {
      // join/leave/start are handled per-device in tick()/lobbyKey(); only the
      // shared "back" comes through the aggregated nav.
      if (nav.back) this.goto("course");
    } else if (this.screen === "profile") {
      this.handleProfilePad(nav);
    } else if (this.screen === "online") {
      if (nav.back) this.onOnlineLeave();
    } else {
      this.handleMusicPad(nav);
    }
  }

  // ---- profile: the pilot's ACT account + Vector Drift record ---------------

  private openProfile(): void {
    this.profileFocus = 0;
    this.nameEdit = null;
    this.goto("profile");
    void pilot.connect(); // tries again if the first sign-in failed
    pilot.refresh();
  }

  private renderProfile(): void {
    this.root.className = "vd-menu overlay vd-screen-profile";
    const st = pilot.state;
    this.content.innerHTML = `
      <div class="vd-shell">
        ${this.plusMarks()}
        ${this.edgeRails("PILOT PROFILE")}
        <header class="vd-topbar">
          <div class="vd-brand vd-brand--garage">
            <span class="vd-badge">${logoMark()}</span>
            <span>
              <span class="vd-brand-name">PROFILE</span>
              <span class="vd-brand-jp">プロフィール</span>
            </span>
          </div>
          <div class="vd-meta">
            <span class="vd-meta-txt">
              <span class="vd-meta-label">ACT ACCOUNT</span>
              <span class="vd-meta-val">${st.status !== "online" ? "OFFLINE" : st.guest ? "GUEST" : "SIGNED IN"}</span>
            </span>
          </div>
        </header>

        <div class="vd-pf-body">
          ${pilotCardHtml(st, pilot.label(), this.profileFocus, this.nameEdit)}
          ${statsHtml(loadStats())}
        </div>

        <footer class="vd-botbar">
          <button class="vd-act back-act"><span class="ring">✕</span> BACK</button>
        </footer>
      </div>`;

    this.content.querySelectorAll<HTMLButtonElement>(".vd-pf-btn").forEach((b, i) => {
      b.addEventListener("click", () => {
        this.profileFocus = i;
        this.profileActivate(b.dataset.act!);
      });
    });
    this.content.querySelector<HTMLButtonElement>(".back-act")!.addEventListener("click", () => this.goto("main"));

    const input = this.content.querySelector<HTMLInputElement>(".vd-pf-input");
    if (input && this.nameEdit) {
      const edit = this.nameEdit;
      input.addEventListener("input", () => (edit.draft = input.value));
      // Keep the game's keyboard (Space, arrows, Esc) out of the field.
      input.addEventListener("keydown", (e) => {
        e.stopPropagation();
        if (e.key === "Enter") void this.saveName();
        else if (e.key === "Escape") this.cancelNameEdit();
      });
      this.content.querySelector<HTMLButtonElement>(".vd-pf-mini.save")!.addEventListener("click", () => void this.saveName());
      this.content.querySelector<HTMLButtonElement>(".vd-pf-mini.cancel")!.addEventListener("click", () => this.cancelNameEdit());
      if (!edit.saving) {
        input.focus();
        input.setSelectionRange(input.value.length, input.value.length);
      }
    }
  }

  private profileActivate(act: string): void {
    if (act === "name") this.startNameEdit();
    else if (act === "account") pilot.openAccount();
  }

  private startNameEdit(): void {
    if (pilot.state.status !== "online") return;
    this.nameEdit = { draft: pilot.state.name ?? "", error: "", saving: false };
    this.renderProfile();
  }

  private cancelNameEdit(): void {
    this.nameEdit = null;
    this.renderProfile();
  }

  private async saveName(): Promise<void> {
    const edit = this.nameEdit;
    if (!edit || edit.saving) return;
    edit.saving = true;
    edit.error = "";
    this.renderProfile();
    const refused = await pilot.setName(edit.draft);
    if (refused) this.sfx("error");
    if (this.nameEdit !== edit) return; // closed meanwhile
    this.nameEdit = refused ? { draft: edit.draft, error: refused, saving: false } : null;
    if (this.screen === "profile") this.renderProfile();
  }

  private handleProfilePad(nav: { up: boolean; down: boolean; left: boolean; right: boolean; confirm: boolean; back: boolean }): void {
    if (pilot.accountOpen()) {
      pilot.padAccount(nav);
      return;
    }
    if (this.nameEdit) {
      if (nav.confirm) void this.saveName();
      if (nav.back) this.cancelNameEdit();
      return;
    }
    if (nav.up || nav.down || nav.left || nav.right) {
      this.profileFocus = this.profileFocus === 0 ? 1 : 0;
      this.renderProfile();
    }
    if (nav.confirm) this.profileActivate(this.profileFocus === 0 ? "name" : "account");
    if (nav.back) this.goto("main");
  }

  /** The account answered or changed: refresh what shows it (never under the player's typing). */
  private onPilotChanged(): void {
    if (this.root.style.display === "none") return;
    if (this.screen === "main") this.renderMain();
    else if (this.screen === "profile" && !this.nameEdit) this.renderProfile();
  }

  // ---- online: the race being joined (ACT HQ t-492) ------------------------

  /** The craft the player picked in the garage (they race it online). */
  get selectedShip(): ShipSpec {
    return getShipById(this.selectedShipId);
  }

  /** Back to MULTIPLAYER from the online screen (the race was left). */
  backFromOnline(): void {
    this.onlineView = null;
    this.goto("mp");
  }

  /** Show (or refresh) the online screen: connecting, the racers so far, or what went wrong. */
  showOnline(view: OnlineView): void {
    this.onlineView = view;
    if (this.screen !== "online") this.goto("online");
    else this.renderOnline();
  }

  private renderOnline(): void {
    this.root.className = "vd-menu overlay vd-screen-online";
    const view = this.onlineView ?? { status: "connecting" as const };
    this.content.innerHTML = `
      <div class="vd-shell">
        ${this.plusMarks()}
        ${this.edgeRails("ONLINE RACE")}
        <header class="vd-topbar">
          <div class="vd-brand vd-brand--garage">
            <span class="vd-badge">${logoMark()}</span>
            <span>
              <span class="vd-brand-name">ONLINE</span>
              <span class="vd-brand-jp">オンライン · QUICK RACE</span>
            </span>
          </div>
          <div class="vd-meta">
            <span class="vd-meta-txt">
              <span class="vd-meta-label">COURSE</span>
              <span class="vd-meta-val">${view.race ? esc(this.trackName(view.race.course)) : "—"}</span>
            </span>
          </div>
        </header>
        ${onlineBodyHtml(view)}
        <footer class="vd-botbar">
          <button class="vd-act back-act"><span class="ring">✕</span> BACK</button>
        </footer>
      </div>`;
    this.content.querySelector<HTMLButtonElement>(".back-act")!.addEventListener("click", () => this.onOnlineLeave());
  }

  /** The waiting line counts down to the bots; called every frame from Game. */
  private refreshOnlineStatus(): void {
    if (this.screen !== "online" || !this.onlineView) return;
    const el = this.content.querySelector(".vd-ol-status");
    const html = onlineBodyHtml(this.onlineView, true);
    if (el && el.innerHTML !== html) el.innerHTML = html;
  }

  // ---- solo submenu --------------------------------------------------------

  private renderSolo(): void {
    this.root.className = "vd-menu overlay vd-screen-solo";
    const rows = SOLO_MODES.map((m, i) => {
      const sub = m.id === "tracks" ? `${m.jp} · ${this.trackName(this.selectedTrackId)}` : m.jp;
      return `
        <button class="vd-mode ${this.soloFocus === i ? "sel" : ""} ${m.playable ? "" : "soon"}" data-solo="${m.id}">
          <span class="vd-ic">${m.icon}</span>
          <span>
            <span class="vd-mode-name">${m.name}</span>
            <span class="vd-mode-jp">${sub}</span>
          </span>
          ${m.playable ? "" : `<span class="soon-tag">SOON</span>`}
        </button>`;
    }).join("");

    this.content.innerHTML = `
      <div class="vd-shell">
        ${this.plusMarks()}
        ${this.edgeRails("SELECT MODE")}
        <header class="vd-topbar">
          <div class="vd-brand vd-brand--garage">
            <span class="vd-badge">${logoMark()}</span>
            <span>
              <span class="vd-brand-name">SOLO</span>
              <span class="vd-brand-jp">ソロ</span>
            </span>
          </div>
        </header>

        <div class="vd-mp-body">${rows}</div>

        <footer class="vd-botbar">
          <button class="vd-act back-act"><span class="ring">✕</span> BACK</button>
        </footer>
      </div>`;

    this.content.querySelectorAll<HTMLButtonElement>(".vd-mode").forEach((b) => {
      b.addEventListener("click", () => this.soloActivate(b.dataset.solo!));
    });
    this.content.querySelector<HTMLButtonElement>(".back-act")!.addEventListener("click", () => this.goto("main"));
  }

  // ---- track builder submenu (simple / tiles / complex) --------------------

  private renderBuild(): void {
    this.root.className = "vd-menu overlay vd-screen-build";
    const rows = BUILD_MODES.map((m, i) => `
        <button class="vd-mode ${this.buildFocus === i ? "sel" : ""}" data-build="${m.id}">
          <span class="vd-ic">${m.icon}</span>
          <span>
            <span class="vd-mode-name">${m.name}</span>
            <span class="vd-mode-jp">${m.jp}</span>
          </span>
        </button>`).join("");

    this.content.innerHTML = `
      <div class="vd-shell">
        ${this.plusMarks()}
        ${this.edgeRails("SELECT MODE")}
        <header class="vd-topbar">
          <div class="vd-brand vd-brand--garage">
            <span class="vd-badge">${logoMark()}</span>
            <span>
              <span class="vd-brand-name">TRACK BUILDER</span>
              <span class="vd-brand-jp">トラックビルダー</span>
            </span>
          </div>
        </header>

        <div class="vd-mp-body">${rows}</div>

        <footer class="vd-botbar">
          <button class="vd-act back-act"><span class="ring">✕</span> ${this.builderOnly ? "PLAY VECTOR DRIFT" : "BACK"}</button>
        </footer>
      </div>`;

    this.content.querySelectorAll<HTMLButtonElement>(".vd-mode").forEach((b) => {
      b.addEventListener("click", () => this.buildActivate(b.dataset.build!));
    });
    this.content.querySelector<HTMLButtonElement>(".back-act")!.addEventListener("click", () => this.buildBack());
  }

  // ---- multiplayer submenu -------------------------------------------------

  private renderMp(): void {
    this.root.className = "vd-menu overlay vd-screen-mp";
    const opt = (idx: number, id: string, name: string, jp: string, soon: boolean) => `
      <button class="vd-mode ${this.mpFocus === idx ? "sel" : ""} ${soon ? "soon" : ""}" data-opt="${id}">
        <span class="vd-ic">${ICONS.mp}</span>
        <span>
          <span class="vd-mode-name">${name}</span>
          <span class="vd-mode-jp">${jp}</span>
        </span>
        ${soon ? `<span class="soon-tag">SOON</span>` : ""}
      </button>`;

    this.content.innerHTML = `
      <div class="vd-shell">
        ${this.plusMarks()}
        ${this.edgeRails("SELECT MODE")}
        <header class="vd-topbar">
          <div class="vd-brand vd-brand--garage">
            <span class="vd-badge">${logoMark()}</span>
            <span>
              <span class="vd-brand-name">MULTIPLAYER</span>
              <span class="vd-brand-jp">マルチプレイヤー</span>
            </span>
          </div>
        </header>

        <div class="vd-mp-body">
          ${opt(0, "local", "LOCAL · SPLIT SCREEN", "ローカル対戦 · 2-4 プレイヤー", false)}
          ${opt(1, "online", "ONLINE · QUICK RACE", "オンライン · クイックレース", false)}
        </div>

        <footer class="vd-botbar">
          <button class="vd-act back-act"><span class="ring">✕</span> BACK</button>
        </footer>
      </div>`;

    this.content.querySelector<HTMLButtonElement>('[data-opt="local"]')!.addEventListener("click", () => {
      this.pending = "local";
      this.goto("course");
    });
    this.content.querySelector<HTMLButtonElement>('[data-opt="online"]')!.addEventListener("click", () => this.onOnline());
    this.content.querySelector<HTMLButtonElement>(".back-act")!.addEventListener("click", () => this.goto("main"));
  }

  // ---- local split-screen join lobby ---------------------------------------

  private static readonly LC_ACCENTS = ["#d8f600", "#00d7f2", "#ff5a1f", "#f4044e"];

  private sameScheme(a: Scheme, b: Scheme): boolean {
    if (a.kind !== b.kind) return false;
    return a.kind !== "gamepad" || a.index === (b as { index: number }).index;
  }
  private slotFor(s: Scheme) {
    return this.lobby.find((x) => this.sameScheme(x.scheme, s));
  }

  /** Advance a device's state: not joined → join (ship-select); selecting →
   * confirm (ready). */
  private lobbyAdvance(s: Scheme): void {
    if (this.screen !== "local") return;
    const slot = this.slotFor(s);
    if (!slot) {
      if (this.lobby.length >= 4) return;
      this.lobby.push({ scheme: s, shipId: SHIPS[this.lobby.length % SHIPS.length].id, ready: false });
    } else if (!slot.ready) {
      slot.ready = true;
    }
    this.renderLocal();
  }

  /** Step a device back: ready → selecting; selecting → leave the slot. */
  private lobbyBack(s: Scheme): void {
    const i = this.lobby.findIndex((x) => this.sameScheme(x.scheme, s));
    if (i < 0) return;
    const slot = this.lobby[i];
    if (slot.ready) slot.ready = false;
    else this.lobby.splice(i, 1);
    this.renderLocal();
  }

  /** Cycle the craft for a device that's still choosing. */
  private lobbyCycle(s: Scheme, dir: number): void {
    const slot = this.slotFor(s);
    if (!slot || slot.ready) return;
    const idx = SHIPS.findIndex((sh) => sh.id === slot.shipId);
    slot.shipId = SHIPS[(idx + dir + SHIPS.length) % SHIPS.length].id;
    this.renderLocal();
  }

  private lobbyCanStart(): boolean {
    return this.lobby.length >= 2 && this.lobby.every((s) => s.ready);
  }
  private beginLocal(): void {
    if (this.screen === "local" && this.lobbyCanStart()) {
      this.onStartLocal(this.lobby.map((s) => ({ scheme: s.scheme, shipId: s.shipId })), this.selectedTrackId);
    }
  }

  /** Keyboard lobby control. P1 = Arrows + Enter (back: Backspace); P2 = A/D +
   * Right-Shift (back: Left-Shift). SPACE starts, ESC exits. */
  private lobbyKey(e: KeyboardEvent): void {
    if (this.screen !== "local") return;
    const arrows: Scheme = { kind: "kbd-arrows" };
    const wasd: Scheme = { kind: "kbd-wasd" };
    switch (e.code) {
      case "Enter": e.preventDefault(); this.lobbyAdvance(arrows); break;
      case "Backspace": e.preventDefault(); this.lobbyBack(arrows); break;
      case "ArrowLeft": e.preventDefault(); this.lobbyCycle(arrows, -1); break;
      case "ArrowRight": e.preventDefault(); this.lobbyCycle(arrows, 1); break;
      case "ShiftRight": e.preventDefault(); this.lobbyAdvance(wasd); break;
      case "ShiftLeft": e.preventDefault(); this.lobbyBack(wasd); break;
      case "KeyA": this.lobbyCycle(wasd, -1); break;
      case "KeyD": this.lobbyCycle(wasd, 1); break;
      case "Space": e.preventDefault(); this.beginLocal(); break;
      case "Escape": this.goto("course"); break;
    }
  }

  /** Polled each frame (from Game) so every gamepad is read individually:
   * A = join/confirm, B = back/leave, D-pad/stick = pick craft, START = begin
   * once all are ready (else join). */
  tick(): void {
    this.refreshOnlineStatus();
    if (this.screen !== "local") return;
    const pads = navigator.getGamepads?.() ?? [];
    for (let i = 0; i < pads.length; i++) {
      const pad = pads[i];
      if (!pad) continue;
      const gp: Scheme = { kind: "gamepad", index: i };
      const a = !!pad.buttons[0]?.pressed;
      const b = !!pad.buttons[1]?.pressed;
      const start = !!pad.buttons[9]?.pressed;
      const ax = pad.axes[0] ?? 0;
      const left = !!pad.buttons[14]?.pressed || ax < -0.5;
      const right = !!pad.buttons[15]?.pressed || ax > 0.5;
      const prev = this.padPrev.get(i) ?? { a: false, b: false, start: false, left: false, right: false };
      if (a && !prev.a) this.lobbyAdvance(gp);
      if (b && !prev.b) this.lobbyBack(gp);
      if (left && !prev.left) this.lobbyCycle(gp, -1);
      if (right && !prev.right) this.lobbyCycle(gp, 1);
      if (start && !prev.start) {
        if (this.lobbyCanStart()) this.beginLocal();
        else this.lobbyAdvance(gp);
      }
      this.padPrev.set(i, { a, b, start, left, right });
    }
  }

  private renderLocal(): void {
    this.root.className = "vd-menu overlay vd-screen-local";
    const canStart = this.lobbyCanStart();

    const slots = Array.from({ length: 4 }, (_, i) => {
      const s = this.lobby[i];
      if (!s) {
        return `
          <div class="vd-lc-player open">
            <span class="vd-lc-tag" style="color:rgba(255,255,255,0.3)">P${i + 1}</span>
            <span class="vd-lc-open">OPEN — press to join</span>
          </div>`;
      }
      const ship = getShipById(s.shipId);
      const name = s.ready ? `${ship.code} <b>${ship.name}</b>` : `◄ ${ship.code} <b>${ship.name}</b> ►`;
      return `
        <div class="vd-lc-player ${s.ready ? "ready" : "select"}">
          <span class="vd-lc-tag" style="color:${Menu.LC_ACCENTS[i]}">P${i + 1}</span>
          <span class="vd-lc-emblem">${shipIcon(ship.id, ship.color.toHexString())}</span>
          <span class="vd-lc-craft">${name}</span>
          <span class="vd-lc-ctl">${schemeLabel(s.scheme)}</span>
          <span class="vd-lc-ready">${s.ready ? "● READY" : "SELECT CRAFT"}</span>
        </div>`;
    }).join("");

    this.content.innerHTML = `
      <div class="vd-shell">
        ${this.plusMarks()}
        ${this.edgeRails("ASSIGN PLAYERS")}
        <header class="vd-topbar">
          <div class="vd-brand vd-brand--garage">
            <span class="vd-badge">${logoMark()}</span>
            <span>
              <span class="vd-brand-name">LOCAL RACE</span>
              <span class="vd-brand-jp">ローカル対戦 · ${this.trackName(this.selectedTrackId)}</span>
            </span>
          </div>
        </header>

        <div class="vd-lc-body">
          <div class="vd-lc-players">${slots}</div>
          <p class="vd-lc-hint">Join, pick your craft with <b>◄ ►</b>, then confirm. Controller: <b>A</b> join/confirm · <b>B</b> back · <b>D-pad</b> craft · <b>START</b> race. Keyboard: P1 <b>Arrows + Enter</b>, P2 <b>A/D + R-Shift</b>. ${canStart ? "Press <b>START</b> / <b>SPACE</b> to race." : "All players must be READY (min 2)."}</p>
        </div>

        <footer class="vd-botbar">
          <button class="vd-act back-act"><span class="ring">✕</span> BACK</button>
          <button class="vd-act start-local ${canStart ? "" : "disabled"}"><span class="ring">▶</span> START</button>
        </footer>
      </div>`;

    this.content.querySelector<HTMLButtonElement>(".start-local")!.addEventListener("click", () => this.beginLocal());
    this.content.querySelector<HTMLButtonElement>(".back-act")!.addEventListener("click", () => this.goto("course"));
  }

  // ---- garage: ship select -------------------------------------------------

  private renderGarage(): void {
    this.root.className = "vd-menu overlay vd-screen-garage";
    const ship = getShipById(this.selectedShipId);

    const seg = (value: number): string => {
      const on = Math.round(value * 8);
      let out = "";
      for (let i = 0; i < 8; i++) out += `<i class="${i < on ? "on" : ""}"></i>`;
      return `<div class="vd-seg">${out}</div>`;
    };
    const stat = (label: string, jp: string, value: number): string => `
      <div class="vd-stat">
        <div><span class="vd-stat-label">${label}</span><span class="vd-stat-jp">${jp}</span></div>
        ${seg(value)}
      </div>`;

    const cards = SHIPS.map(
      (s) => `
      <button class="vd-card ${s.id === this.selectedShipId ? "sel" : ""}" data-id="${s.id}">
        ${shipIcon(s.id, hex(s))}
        <span class="vd-card-name">${s.code} // ${s.name}</span>
      </button>`
    ).join("");

    this.content.innerHTML = `
      <div class="vd-shell">
        ${this.plusMarks()}

        <header class="vd-topbar">
          <div class="vd-brand vd-brand--garage">
            <span class="vd-badge">${logoMark()}</span>
            <span>
              <span class="vd-brand-name">${this.racing() ? "SELECT CRAFT" : "GARAGE"}<span class="vd-hatch-pink" style="width:18vh;max-width:24vw;height:1.3vh;display:inline-block"></span></span>
              <span class="vd-brand-jp">${this.racing() ? `機体選択 · ${this.trackName(this.selectedTrackId)}` : "ガレージ"}</span>
            </span>
          </div>
          <div class="vd-meta">
            <span class="vd-meta-txt">
              <span class="vd-meta-label">CREDITS</span>
              <span class="vd-meta-val">12,450</span>
            </span>
            <span class="vd-plusbox">+</span>
          </div>
        </header>

        <div class="vd-garage-body">
          <aside class="vd-statcard">
            <span class="arrow">↗</span>
            <div class="vd-sc-name">${ship.code} // ${ship.name}</div>
            <div class="vd-sc-jp">${ship.jp}</div>
            ${stat("TURNING", "旋回性能", ship.cornering)}
            ${stat("ACCELERATION", "加速", ship.acceleration)}
            ${stat("TOP SPEED", "最高速度", ship.topSpeed)}
            ${stat("WEIGHT", "重量", ship.weight)}
            <span class="corner"></span>
          </aside>

          <div class="vd-garage-hero"></div>

          <div class="vd-sidenav">
            <span class="tab">${ship.code}</span>
            <span class="num">20</span>
            <span class="ico vd-ic">${ICONS.mp}</span>
            <span class="vd-plus" style="position:static">◆</span>
          </div>
        </div>

        <div class="vd-roster">
          <button class="nav prev">‹</button>
          <div class="vd-cards">${cards}</div>
          <button class="nav next">›</button>
        </div>

        <footer class="vd-botbar">
          <button class="vd-act back-act"><span class="ring">✕</span> BACK</button>
          <button class="vd-act select-act">${this.racing() ? "RACE ▶" : "SELECT"} <span class="ring">✕</span></button>
        </footer>
      </div>`;

    this.content.querySelectorAll<HTMLButtonElement>(".vd-card").forEach((btn) => {
      btn.addEventListener("click", () => {
        this.selectedShipId = btn.dataset.id!;
        this.render();
      });
    });
    this.content.querySelector<HTMLButtonElement>(".prev")!.addEventListener("click", () => this.cycle(-1));
    this.content.querySelector<HTMLButtonElement>(".next")!.addEventListener("click", () => this.cycle(1));
    this.content.querySelector<HTMLButtonElement>(".back-act")!.addEventListener("click", () => this.goto(this.racing() ? "course" : "main"));
    this.content.querySelector<HTMLButtonElement>(".select-act")!.addEventListener("click", () => {
      if (this.racing()) this.startPending();
      else this.goto("main");
    });
  }

  /** In the craft step of a solo race set-up (vs browsing the garage). */
  private racing(): boolean {
    return this.pending === "quick" || this.pending === "time";
  }

  private cycle(dir: number): void {
    const idx = SHIPS.findIndex((s) => s.id === this.selectedShipId);
    const next = (idx + dir + SHIPS.length) % SHIPS.length;
    this.selectedShipId = SHIPS[next].id;
    this.render();
  }

  // ---- course select: mode → COURSE → craft -----------------------------------

  private renderCourse(): void {
    this.root.className = "vd-menu overlay vd-screen-tracks";
    const mode = this.pending === "time" ? "TIME ATTACK" : this.pending === "local" ? "LOCAL RACE" : "QUICK RACE";
    const cards = TRACKS.map(
      (t) => `
      <button class="vd-track-card ${t.id === this.selectedTrackId ? "sel" : ""}" data-id="${t.id}">
        <span class="vd-track-prev">${this.previewHtml(t)}</span>
        <span class="vd-track-name">${t.name}</span>
        <span class="vd-track-jp">${t.jp ?? ""}</span>
      </button>`
    ).join("");

    this.content.innerHTML = `
      <div class="vd-shell">
        ${this.plusMarks()}
        ${this.edgeRails("CHOOSE COURSE")}
        <header class="vd-topbar">
          <div class="vd-brand vd-brand--garage">
            <span class="vd-badge">${logoMark()}</span>
            <span>
              <span class="vd-brand-name">SELECT COURSE</span>
              <span class="vd-brand-jp">コース選択 · ${mode}</span>
            </span>
          </div>
        </header>

        <div class="vd-track-grid">${cards}</div>

        <footer class="vd-botbar">
          <button class="vd-act back-act"><span class="ring">✕</span> BACK</button>
          <button class="vd-act select-act">NEXT <span class="ring">✕</span></button>
        </footer>
      </div>`;

    // Tap a course to pick it; tap it again (or NEXT) to go on to the craft.
    this.content.querySelectorAll<HTMLButtonElement>(".vd-track-card").forEach((btn) => {
      btn.addEventListener("click", () => {
        if (btn.dataset.id === this.selectedTrackId) this.courseNext();
        else {
          this.selectedTrackId = btn.dataset.id!;
          this.renderCourse();
        }
      });
    });
    this.content.querySelector<HTMLButtonElement>(".back-act")!.addEventListener("click", () => this.courseBack());
    this.content.querySelector<HTMLButtonElement>(".select-act")!.addEventListener("click", () => this.courseNext());
    startTrackPreviews(TRACKS, () => this.onPreviewsReady());
  }

  /** The card art: the spinning 3D mini-course once the previews are live,
   * the flat outline until then. */
  private previewHtml(t: TrackSpec): string {
    return previewsLive() ? previewCanvas(t.id) : trackThumb(t.points);
  }

  /** Swap the cards' outlines for the live previews as soon as they draw. */
  private onPreviewsReady(): void {
    if (this.screen !== "course") return;
    for (const t of TRACKS) {
      const slot = this.content.querySelector<HTMLElement>(`.vd-track-card[data-id="${t.id}"] .vd-track-prev`);
      if (slot) slot.innerHTML = this.previewHtml(t);
    }
  }

  /** Move the course cursor through the grid (±1 across, ±COURSE_COLS down). */
  private moveCourse(step: number): void {
    const idx = TRACKS.findIndex((t) => t.id === this.selectedTrackId);
    const next = idx + step;
    if (next < 0 || next >= TRACKS.length) return;
    this.selectedTrackId = TRACKS[next].id;
    this.renderCourse();
  }

  // ---- music: per-track pool curation --------------------------------------

  private renderMusic(): void {
    this.root.className = "vd-menu overlay vd-screen-music";
    const tracks = this.audio.tracks();
    const np = this.audio.currentTrack;

    const rows = tracks
      .map((t, i) => {
        const menuOn = this.audio.isEnabled(t, "menu");
        const raceOn = this.audio.isEnabled(t, "race");
        const foc = this.musicFocus === i + 1 ? "foc" : "";
        return `
        <div class="vd-mus-row ${foc}" data-i="${i}">
          <span class="vd-mus-meta"><b>${t.title}</b><span>${t.artist}</span></span>
          <button class="vd-mus-tog ${menuOn ? "on" : ""}" data-i="${i}" data-pool="menu">MENU</button>
          <button class="vd-mus-tog ${raceOn ? "on" : ""}" data-i="${i}" data-pool="race">RACE</button>
        </div>`;
      })
      .join("");

    this.content.innerHTML = `
      <div class="vd-shell">
        ${this.plusMarks()}
        <header class="vd-topbar">
          <div class="vd-brand vd-brand--garage">
            <span class="vd-badge">${logoMark()}</span>
            <span>
              <span class="vd-brand-name">MUSIC</span>
              <span class="vd-brand-jp">ミュージック</span>
            </span>
          </div>
          <div class="vd-mus-now">
            <span class="vd-mus-now-k">NOW PLAYING <i>再生中</i></span>
            <span class="vd-mus-now-t">${np ? `${np.title} — ${np.artist}` : "—"}</span>
            <button class="vd-mus-skip ${this.musicFocus === 0 ? "foc" : ""}">⏭ SKIP</button>
          </div>
        </header>

        <div class="vd-mus-vol">
          <label>MUSIC <input type="range" class="vd-musvol" min="0" max="1" step="0.05" value="${this.audio.musicVolume}"><b class="vd-musvol-v">${Math.round(this.audio.musicVolume * 100)}</b></label>
          <label>GAME AUDIO <input type="range" class="vd-sfxvol" min="0" max="1" step="0.05" value="${this.audio.sfxVolume}"><b class="vd-sfxvol-v">${Math.round(this.audio.sfxVolume * 100)}</b></label>
        </div>
        <p class="vd-mus-hint">Choose which tracks play in the MENU pool and the RACE pool. Disabled tracks are skipped in that context.</p>
        <div class="vd-mus-list">${rows}</div>

        <footer class="vd-botbar">
          <button class="vd-act back-act"><span class="ring">✕</span> BACK</button>
        </footer>
      </div>`;

    this.content.querySelectorAll<HTMLButtonElement>(".vd-mus-tog").forEach((b) => {
      b.addEventListener("click", () => {
        const i = parseInt(b.dataset.i!, 10);
        const pool = b.dataset.pool as Pool;
        const on = !this.audio.isEnabled(tracks[i], pool);
        this.audio.setEnabled(tracks[i], pool, on);
        this.sfx(on ? "toggle-on" : "toggle-off");
        b.classList.toggle("on", on);
      });
    });
    this.content.querySelector<HTMLButtonElement>(".vd-mus-skip")!.addEventListener("click", () => {
      this.audio.skip();
      this.refreshNowPlaying();
    });
    const mv = this.content.querySelector<HTMLInputElement>(".vd-musvol");
    mv?.addEventListener("input", () => {
      const v = parseFloat(mv.value);
      this.audio.setMusicVolume(v);
      const b = this.content.querySelector(".vd-musvol-v");
      if (b) b.textContent = String(Math.round(v * 100));
    });
    const sv = this.content.querySelector<HTMLInputElement>(".vd-sfxvol");
    sv?.addEventListener("input", () => {
      const v = parseFloat(sv.value);
      this.audio.setSfxVolume(v);
      const b = this.content.querySelector(".vd-sfxvol-v");
      if (b) b.textContent = String(Math.round(v * 100));
    });
    this.content.querySelector<HTMLButtonElement>(".back-act")!.addEventListener("click", () => this.goto("main"));
  }

  private refreshNowPlaying(): void {
    const el = this.content.querySelector(".vd-mus-now-t");
    const np = this.audio.currentTrack;
    if (el) el.textContent = np ? `${np.title} — ${np.artist}` : "—";
  }

  private scrollMusicFocus(): void {
    const el = this.content.querySelector(".vd-mus-row.foc, .vd-mus-skip.foc");
    el?.scrollIntoView({ block: "nearest" });
  }

  private handleMusicPad(nav: { up: boolean; down: boolean; left: boolean; right: boolean; confirm: boolean; back: boolean }): void {
    if (nav.back) {
      this.goto("main");
      return;
    }
    const n = this.audio.tracks().length;
    if (nav.up || nav.down) {
      const slots = n + 1; // skip button + one per track
      this.musicFocus = (this.musicFocus + (nav.down ? 1 : -1) + slots) % slots;
      this.renderMusic();
      this.scrollMusicFocus();
      return;
    }
    if (this.musicFocus === 0) {
      if (nav.confirm) {
        this.audio.skip();
        this.refreshNowPlaying();
      }
      return;
    }
    const t = this.audio.tracks()[this.musicFocus - 1];
    if (nav.left || nav.confirm) {
      this.sfx(this.audio.isEnabled(t, "menu") ? "toggle-off" : "toggle-on");
      this.audio.setEnabled(t, "menu", !this.audio.isEnabled(t, "menu"));
      this.renderMusic();
      this.scrollMusicFocus();
    } else if (nav.right) {
      this.sfx(this.audio.isEnabled(t, "race") ? "toggle-off" : "toggle-on");
      this.audio.setEnabled(t, "race", !this.audio.isEnabled(t, "race"));
      this.renderMusic();
      this.scrollMusicFocus();
    }
  }

  private goto(screen: Screen): void {
    if (screen === "local") {
      this.lobby = [];
      this.padPrev.clear();
    }
    this.screen = screen;
    this.entering = true;
    this.render();
  }

  show(v: boolean): void {
    if (v) {
      this.screen = this.builderOnly ? "build" : "main";
      this.entering = true;
    }
    this.root.style.display = v ? "" : "none";
    this.preview.setActive(v);
    if (v) this.render();
  }
}
